import React from 'react';
import moment from 'moment-timezone';
import { BooleanView, getTableColumn, Select, TableColumn } from '@shared/UI';
import { ISettingsTransactionsDto } from '@shared/api';
import { IconType } from 'react-icons';
import { FilterArrowsIcon } from '@shared/icons/FilterArrowsIcon';
import { useTransactionsTable } from './TransactionsTableProvider';
import { getHeaderClassName } from '@shared/UI/SimpleTable/lib/getHeaderClassName';
import { HeaderCellWithFilter } from '@shared/UI/SimpleTable/components/HeaderCellWithFilter';
import { formats, mapLookupsToSelectOptions } from '@shared/utils';
import { useLookups } from '@shared/providers/LookupsProvider';

export function getTransactionsTableColumns(): TableColumn<ISettingsTransactionsDto>[] {
    const { desc, sortFieldName, handleTransactionsSort, transactionTypes, setTransactionTypes, arrayWithLogos } =
        useTransactionsTable();
    const { lookups } = useLookups();
    const transactionTypeOptions = lookups && mapLookupsToSelectOptions('TransactionType', lookups);
    const transactionTypesLookup = lookups?.find((lookup) => lookup.name === 'TransactionType')?.items;

    return [
        getTableColumn({
            dataField: 'id',
            flex: 0.3,
            firstColumn: true,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleTransactionsSort('id')}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'id')}`,
        }),
        getTableColumn({
            title: 'Symbol',
            flex: 0.5,
            formatter: (_, row) =>
                row.symbol ? (
                    <div className='transactions-table-cell'>
                        {arrayWithLogos.find((item) => item.symbol === row.symbol)?.link ? (
                            <img
                                className='transactions-table-cell__logos'
                                src={arrayWithLogos.find((item) => item.symbol === row.symbol)?.link}
                            />
                        ) : (
                            <div className='transactions-table-cell__logos placeholder' />
                        )}
                        <span>{row.symbol}</span>
                    </div>
                ) : (
                    '-'
                ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'id')}`,
        }),
        getTableColumn({
            dataField: 'clearingAccount',
            title: 'Account Number',
            flex: 0.3,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleTransactionsSort('accountId')}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'accountId')}`,
        }),
        getTableColumn({
            dataField: 'orderId',
            flex: 0.3,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleTransactionsSort('orderId')}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'orderId')}`,
        }),
        getTableColumn({
            dataField: 'date',
            flex: 0.7,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleTransactionsSort('date')}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'date')}`,
            formatter: (data) => moment(String(data)).format(formats.fullTime),
        }),
        getTableColumn({
            dataField: 'settlementDate',
            flex: 0.7,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleTransactionsSort('settlementDate')}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'settlementDate')}`,
            formatter: (data) => (data ? moment(String(data)).format(formats.fullTime) : '-'),
        }),
        getTableColumn({
            dataField: 'price',
            flex: 0.4,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleTransactionsSort('price')}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'price')}`,
        }),
        getTableColumn({
            dataField: 'quantity',
            flex: 0.4,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleTransactionsSort('quantity')}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'quantity')}`,
        }),
        getTableColumn({
            dataField: 'type',
            flex: 0.5,
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'type')}`,
            formatter: (cell) => {
                const type = transactionTypesLookup?.find((option) => option.id === cell);
                return type?.displayName || type?.name;
            },
            headerFormatter: () => (
                <Select
                    currentValue={transactionTypes}
                    options={transactionTypeOptions || []}
                    dropdownPlacement='bottom-right'
                    placeholder='Type'
                    multiple
                    nonInputView
                    withClearButton
                    onSelect={setTransactionTypes}
                />
            ),
        }),
        getTableColumn({
            dataField: 'processed',
            flex: 0.4,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleTransactionsSort('processed')}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'processed')}`,
            formatter: (data) => <BooleanView is={!!data} />,
        }),
    ];
}
